import {Component, OnInit} from '@angular/core';
import {AtomicInventoryRow, RecipeRow, WaxService} from './wax-service.service';
import {NEVER, Observable} from 'rxjs';
import {map, tap, withLatestFrom} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  title = 'auto-unbox';
  inventory$: Observable<AtomicInventoryRow[]>;
  packInventory$: Observable<AtomicInventoryRow[]>;
  recipes$: Observable<RecipeRow[]>;
  recipeCost: string;
  recipeAssets: AtomicInventoryRow[];

  constructor(public waxService: WaxService, private snackBar: MatSnackBar) {}


  waxLogin(): void {
    if (!this.waxService.userAccount) {
      this.waxService.waxLogin().subscribe((result: string) => {
        this.loggedIn();
      }, (error: Error) => {
        if (error.message.includes('popup')) {
          alert('Failed to open the log-in pop-up ! Please unblock pop-ups for this domain and try again.');
        }
      });
    }
  }

  scatterLogin(): void {
    if (!this.waxService.userAccount) {
      this.waxService.scatterLogin().subscribe((result: string) => {
        this.loggedIn();
      }, (error: Error) => {
        if (error.message.includes('popup')) {
          alert('Failed to open the log-in pop-up ! Please unblock pop-ups for this domain and try again.');
        }
      });
    }
  }

  anchorLogin(): void {
    if (!this.waxService.userAccount) {
      this.waxService.anchorLogin().subscribe((result: string) => {
        console.log(result);
        this.loggedIn();
      }, (error: Error) => {
        if (error.message.includes('popup')) {
          alert('Failed to open the log-in pop-up ! Please unblock pop-ups for this domain and try again.');
        }
      });
    }
  }

  loggedIn(): void {
    this.inventory$ = this.waxService.loadAtomicInventory();

    this.packInventory$ = this.inventory$.pipe(
      map(inventory => {
        return inventory.filter(asset => {
          return [55356, 55359].includes(parseInt(asset.template.template_id, 10));
        });
      })
    );
    this.recipes$ = this.waxService.loadRecipes().pipe(
      withLatestFrom(this.inventory$),
      map(([recipes, inventory]) => {
        return recipes.filter(recipe => {
          return recipe.requirements.reduce((prev, curr) => {
            let nbReq = 0;
            inventory.forEach((value) => {
              if (parseInt(value.template.template_id, 10) === curr.template_id) {
                nbReq++;
              }
            });
            return prev && nbReq >= curr.quantity;
          }, true);
        });
      })
    );
  }

  unboxPack(pack: AtomicInventoryRow): void {
    const { asset_id, template: { template_id }, collection: { collection_name } } = pack;
    const actions = [
      {
        account: 'atomicassets',
        name: 'transfer',
        authorization: [
          {
            actor: this.waxService.userAccount,
            permission: 'active',
          },
        ],
        data: {
          from: this.waxService.userAccount,
          to: 'unbox.nft',
          asset_ids: [asset_id],
          memo: 'open pack',
        },
      },
      {
        account: 'unbox.nft',
        name: 'unbox',
        authorization: [
          {
            actor: this.waxService.userAccount,
            permission: 'active',
          },
        ],
        data: {
          collection_name,
          from: this.waxService.userAccount,
          box_id: template_id,
        },
      }
    ];
    this.waxService.transact(actions).subscribe(transaction => {
      this.snackBar.open('Unboxed pack, waiting for claim...', null, {duration: 2000});
      this.waxService.loadPendingClaim(asset_id, 'unbox.nft').subscribe(result => {
        const claimActions = [
          {
            account: 'unbox.nft',
            name: 'claim',
            authorization: [
              {
                actor: this.waxService.userAccount,
                permission: 'active',
              },
            ],
            data: {
              collection_name,
              from: this.waxService.userAccount,
              assoc_id: asset_id,
            },
          }
        ];
        this.waxService.transact(claimActions).subscribe(claimTransaction => {
          this.snackBar.open('Pack claimed !', null, {duration: 2000});
          console.log('Claimed pack !', claimTransaction);
        }, error => {
          console.error(error);
          this.snackBar.open(error, null, {duration: 2000});
        });
      });
    }, error => {
      console.error(error);
      this.snackBar.open(error, null, {duration: 2000});
    });
  }

  recipeSelected(recipe: RecipeRow, inventory: AtomicInventoryRow[]): void {
    let assets = [];
    recipe.requirements.forEach(requirement => {
      const goodAssets = inventory.filter((value) => {
        return parseInt(value.template.template_id, 10) === requirement.template_id;
      });
      goodAssets.sort((a, b) => {
        return parseInt(b.template_mint, 10) - parseInt(a.template_mint, 10);
      });
      assets = assets.concat(goodAssets.slice(0, requirement.quantity));
    });
    console.log('Assets found for craft : ', assets);
    this.recipeCost = 'This craft will consume mints : ' + assets.map(asset => ` #${asset.template_mint} and`).join('').slice(0, -3);
    this.recipeAssets = assets;
  }

  craft(recipe): void {
    if (!this.recipeAssets) {
      console.error('Cannot craft because recipe is not saved !!!');
      return;
    }
    const actions = [
      {
        account: 'atomicassets',
        name: 'transfer',
        authorization: [
          {
            actor: this.waxService.userAccount,
            permission: 'active',
          },
        ],
        data: {
          from: this.waxService.userAccount,
          to: 'craft.nft',
          asset_ids: this.recipeAssets.map(value => value.asset_id),
          memo: 'cardlab craft',
        },
      },
      {
        account: 'craft.nft',
        name: 'craft',
        authorization: [
          {
            actor: this.waxService.userAccount,
            permission: 'active',
          },
        ],
        data: {
          collection_name: 'stf.capcom',
          from: this.waxService.userAccount,
          recipe_id: recipe.recipe_id,
        },
      }
    ];
    this.waxService.transact(actions).subscribe(transaction => {
      const { processed: { action_traces: [transferTrace, craftingTrace] } } = transaction;
      if (craftingTrace) {
        const { inline_traces } = craftingTrace;
        for (const trace of inline_traces) {
          const { act: { account, name, data } } = trace;
          if (account === 'orng.wax' && name === 'requestrand') {
            if (data.assoc_id) {
              this.snackBar.open('Crafted cards, waiting for claim...', null, {duration: 2000});
              this.waxService.loadPendingClaim(data.assoc_id, 'craft.nft').subscribe(result => {
                const claimActions = [
                  {
                    account: 'craft.nft',
                    name: 'claim',
                    authorization: [
                      {
                        actor: this.waxService.userAccount,
                        permission: 'active',
                      },
                    ],
                    data: {
                      collection_name: 'stf.capcom',
                      from: this.waxService.userAccount,
                      assoc_id: data.assoc_id,
                    },
                  }
                ];
                this.waxService.transact(claimActions).subscribe(claimTransaction => {
                  this.snackBar.open('Claimed crafted cards !', null, {duration: 2000});
                  console.log('Claimed crafted cards !', claimTransaction);
                }, error => {
                  console.error(error);
                  this.snackBar.open(error, null, {duration: 2000});
                });
              });
            }
          }
        }
      }
    }, error => {
      console.error(error);
      this.snackBar.open(error, null, {duration: 2000});
    });
  }
}
